export interface ComputeColumnWidthsOptions {
  /** When true, distribute leftover horizontal space so visible columns fill the table width. */
  readonly fill?: boolean;
  /** Columns to exclude from the fill calculation (they occupy no rendered space). */
  readonly hiddenColumnNames?: string[];
}

// Shared max-width + no-wrap presets for columns whose content has a known,
// compact shape. `bool`/`date` columns get their preset automatically (from
// filterType); state-abbreviation and single-icon columns opt in via the
// column's `sizeHint`. Widths reuse the existing size tokens (see
// columnWidthMapping): xs ≈ 70-80px, s ≈ 90-120px, xxs = 50px.
// `satisfies` (not a `Record<string, …>` annotation) so the literal keys are
// preserved for `ColumnSizeHint = keyof typeof SIZE_HINT_PRESETS` in
// table.types.ts — the public hint type is derived from this map and can't
// drift from it again.
export const SIZE_HINT_PRESETS = {
  boolean: { width: 'xs', multiline: false }, // Yes/No
  date: { width: 'date', multiline: false }, // "2m ago" / absolute dates
  stateAbbr: { width: 'xs', multiline: false }, // CA, TX
  icon: { width: 'xxs', multiline: false }, // single-icon cells
  // Notes cells hold one small icon AND need to show the "Notes" header label, so `xxs` (50px)
  // truncated the header. `xs` is the smallest token that renders it: the label is five
  // characters, and `s` left the column more than half empty on every listing that has one.
  notes: { width: 'xs', multiline: false },
  // Identifier columns (quote/policy/claim/submission numbers) and status-enum
  // columns must render on a single line — wrapping them makes the row taller
  // for no benefit. `m` (140-250px) leaves room for the full value
  // ("CL02-PLM-PR-0Z7KJTV87" / "SUBMITTED" / "IN_REVIEW") without wrapping.
  identifier: { width: 'ref', multiline: false },
  status: { width: 'enum', multiline: false },
} satisfies Record<string, { width: string; multiline: boolean }>;

// Exact `name`s of the reference-number columns that must never wrap. These are
// short, opaque-ish identifiers shown across many tables.
const IDENTIFIER_COLUMN_NAMES = new Set([
  'quoteNumber',
  'policyNumber',
  'claimNumber',
  'submissionNumber',
  // Same shape as the rest -- "INV40605", "CL01-PLM-SYTGNCGZE" -- and it sits beside claimNumber
  // on every invoice listing, where one wrapped and the other did not.
  'invoiceNumber',
]);

// A status column holds a single enum value that should stay on one line.
// Matches `status` and any `*Status` variant (agencyStatus, submissionStatus,
// claimStatus, customerStatus, ...).
const isStatusColumnName = (name: unknown): boolean =>
  typeof name === 'string' && name.toLowerCase().endsWith('status');

const resolveSizeHint = (column: any): string | undefined => {
  if (column.sizeHint && SIZE_HINT_PRESETS[column.sizeHint]) {
    return column.sizeHint;
  }
  if (column.filterType === 'bool') {
    return 'boolean';
  }
  if (column.filterType === 'date') {
    return 'date';
  }
  // A "notes" column globally gets the compact notes preset (wide enough for
  // the "Notes" header + its icon(s), far narrower than the old auto width).
  // Still only a DEFAULT: any table that sets its own `width` keeps it.
  if (column.name === 'notes' || column.name === 'note') {
    return 'notes';
  }
  // Identifier + status columns never wrap. Still a DEFAULT: an explicit
  // `width`/`multiline` on the column always wins (see applyColumnSizePreset).
  if (IDENTIFIER_COLUMN_NAMES.has(column.name)) {
    return 'identifier';
  }
  if (isStatusColumnName(column.name)) {
    return 'status';
  }
  return undefined;
};

// Apply the size preset as DEFAULTS only — an explicit `width`/`multiline` on
// the column always wins, so existing bespoke sizing is never overridden.
export const applyColumnSizePreset = (column: any) => {
  const hint = resolveSizeHint(column);
  if (!hint) {
    return column;
  }
  const preset = SIZE_HINT_PRESETS[hint];
  return {
    ...column,
    width: column.width ?? preset.width,
    multiline: column.multiline ?? preset.multiline,
  };
};

export const mapColumnExtensions = (
  columns: any,
  tableWidth: any,
  options: ComputeColumnWidthsOptions = {}
) => {
  const sized = columns.map(applyColumnSizePreset);
  const widths = computeColumnWidths(sized, tableWidth, options);
  return sized.map((column: any) => ({
    columnName: column.name,
    wordWrapEnabled: column.multiline !== false,
    align: column.align || 'left',
    width: widths[column.name],
    minWidth: 90,
  }));
};

/** What a column with no width of its own gets: the 15rem it has always been at the design root. */
const UNSIZED_COLUMN_REM = 15;

export const calculateColumnWidth = (column: any, tableWidth: any) => {
  if (column.width === 'auto') {
    return 'auto';
  }
  if (!column.width) {
    return remToPx(UNSIZED_COLUMN_REM);
  }
  if (typeof column.width === 'number') {
    return column.width;
  }
  // An explicit rem width, for a column that wants its own number rather than a token. Tokens
  // are for a shape shared by many columns; coupling two columns to one token because they
  // happen to want the same size means a change made for one silently moves the other.
  if (typeof column.width === 'string' && column.width.endsWith('rem')) {
    return remToPx(parseFloat(column.width));
  }
  if (typeof column.width === 'string' && column.width.endsWith('%')) {
    const factor =
      Number(column.width.slice(0, column.width.length - 1)) * 0.01;
    return tableWidth * factor;
  }
  if (
    typeof column.width === 'string' &&
    Object.prototype.hasOwnProperty.call(columnWidthMapping, column.width)
  ) {
    const { pct, min, max } = columnWidthMapping[column.width];
    const percent = pct * tableWidth;
    const minPx = remToPx(min);
    const maxPx = remToPx(max);

    if (percent < minPx) {
      return minPx;
    }
    if (percent > maxPx) {
      return maxPx;
    }
    return percent;
  }
  throw new Error(
    `Invalid value provided for "column.width": { columnName: ${column.name}, width: ${column.width} }`
  );
};

/**
 * The root font the console designs against. It pins the document to 12px and re-bases it at
 * 1620 and 1920, so this is the root every rem below is measured in.
 */
const DESIGN_ROOT_PX = 12;

/** The root the document is actually on, which is what a rem resolves against right now. */
export const currentRootPx = (): number => {
  if (typeof document === 'undefined') {
    return DESIGN_ROOT_PX;
  }
  const root = parseFloat(
    getComputedStyle(document.documentElement).fontSize || ''
  );
  return Number.isFinite(root) && root > 0 ? root : DESIGN_ROOT_PX;
};

/** rem -> the px dev-express needs, against whatever root the console has re-based to. */
export const remToPx = (value: number): number =>
  value === Infinity ? Infinity : value * currentRootPx();

/**
 * Column size tokens, in rem.
 *
 * <p>`pct` is a share of the table's width and scales on its own. The bounds are the part that
 * used to be fixed px: a column clamped at 120px held its size while the text inside it grew by
 * a third at 1920, so the label stayed put and the value it labelled did not fit. In rem the
 * clamp tracks the text.
 */
const columnWidthMapping = {
  xxs: { pct: 0, min: 4 },
  xs: { pct: 0.055, min: 6, max: 6.5 },
  s: { pct: 0.073, min: 7.5, max: 10 },
  // Fixed tokens, for columns whose content does not grow with the table. A percentage of the
  // table width is the wrong rule for these: a date is a date, a claim number is a claim number,
  // and a wider window does not make either of them longer -- it only leaves the column emptier.
  // Each number below is the widest thing the column holds (its value, or its header where that
  // is longer) plus the 1.33rem a header cell loses to the resize handle and the 1.33rem of cell
  // padding, all measured in the rendered table.
  //
  // Each is the widest VALUE the column holds plus the cell's 1rem of padding, measured in the
  // rendered table. Deliberately not the header: a header that truncates costs a word somebody
  // already knows, while a value that truncates costs the thing they came to read.
  //
  //   date  "May 29, 2025" is 5.95rem
  //   ref   "CL09-PLM-SMMEL5G5O" is 10.85rem
  //   enum  "Within Retention" is 7.3rem
  //   money "$6,000,000.00" is 6.28rem
  date: { pct: 0, min: 7 },
  ref: { pct: 0, min: 12 },
  enum: { pct: 0, min: 9 },
  money: { pct: 0, min: 7.5 },
  // A cell of controls -- a Review button beside the claim-summary and row-menu icons -- measured
  // at 12.5rem in the rendered table with the cell's own padding. Fixed, and deliberately not `s`:
  // a flexible token would absorb the table's leftover width and strand the buttons in the middle
  // of an empty cell, which is what the claims listing's Action column did before it had a width.
  // Just the row menu and the expand chevron, no button -- 5.34rem of controls plus the cell's own
  // padding. Sized to its contents on purpose: leftover width is split either side of the centred
  // group, so a token meant for a cell with a button strands two icons in the middle of it.
  actionsIcons: { pct: 0, min: 6.6 },
  // A row menu's one icon and nothing else: the icon button plus the cell's own padding.
  actionsMenu: { pct: 0, min: 3.5 },
  actions: { pct: 0, min: 12.5 },
  // The same cell with a longer button -- "RE Details" against "Review". Its own token rather than
  // widening `actions`, which would leave the claims column padded out with empty space.
  actionsWide: { pct: 0, min: 15 },
  // One button sized to its label and nothing else, on the Claims list's Action column, measured
  // in the rendered table: "COMPLETE FILING" is 9.4rem and "ADD DETAILS" (`actionsButtonShort`)
  // 7.06rem, each plus the cell's 1rem of padding and 0.25rem for sub-pixel rounding: at the bare
  // sum the label wraps.
  actionsButton: { pct: 0, min: 10.65 },
  actionsButtonShort: { pct: 0, min: 8.3 },
  m: { pct: 0.13, min: 11.5, max: 21 },
  l: { pct: 0.18, min: 18, max: 30 },
  xl: { pct: 0.3, min: 0, max: Infinity },
};

/**
 * Size tokens whose columns hold content and should absorb leftover width when
 * `fill` is on. Fixed-pixel columns and the tiny icon tokens (`xxs`, `xs`) stay
 * put — they are used for star/eye/options/flag cells that should not stretch.
 */
const FLEXIBLE_WIDTH_TOKENS = new Set(['s', 'm', 'l', 'xl']);

/**
 * Tokens sized to the controls a cell holds rather than to its content. The width belongs to
 * the code, not to the user: when the controls change, the token changes with them.
 */
export const CONTROL_SIZED_WIDTH_TOKENS: ReadonlySet<string> = new Set([
  'actionsIcons',
  'actionsMenu',
  'actions',
  'actionsWide',
  'actionsButton',
  'actionsButtonShort',
]);

/** Whether a width token is one of `columnWidthMapping`'s keys. For tests of the set above. */
export const isColumnWidthToken = (token: string): boolean =>
  Object.prototype.hasOwnProperty.call(columnWidthMapping, token);

/**
 * Drop saved widths for columns sized by a control token.
 *
 * <p>`useUiSettingsAutosave` writes every column's width on every mount, not only after a
 * drag, so a saved width is usually the width the code gave the column on the user's last
 * visit. Saved widths win over defaults, so when a column moves to a control token the old
 * width would stay: the agency Accounts menu column kept a saved 75px after its cell gained
 * the chevron and needed `actionsIcons`, and its controls overflowed. For these columns the
 * config's width is always the right one.
 */
export const dropControlSizedSavedWidths = <T extends { columnName: string }>(
  saved: readonly T[],
  columns: readonly { name: string; width?: unknown }[]
): T[] => {
  const controlSized = new Set(
    columns
      .filter(
        (column) =>
          typeof column.width === 'string' &&
          CONTROL_SIZED_WIDTH_TOKENS.has(column.width)
      )
      .map((column) => column.name)
  );
  return saved.filter((entry) => !controlSized.has(entry.columnName));
};

const isFlexibleColumn = (column: any) =>
  column.width === undefined ||
  column.width === null ||
  FLEXIBLE_WIDTH_TOKENS.has(column.width);

/**
 * The columns fill measures: visible, and sized in pixels rather than `auto`.
 */
const visibleNumericColumns = (
  columns: any[],
  baseWidths: Record<string, number | 'auto'>,
  hiddenColumnNames: readonly string[] = []
) => {
  const hidden = new Set(hiddenColumnNames);
  return columns.filter(
    (column) =>
      !hidden.has(column.name) && typeof baseWidths[column.name] === 'number'
  );
};

const totalColumnWidth = (
  columns: any[],
  baseWidths: Record<string, number | 'auto'>
) =>
  columns.reduce(
    (acc: number, column: any) => acc + (baseWidths[column.name] as number),
    0
  );

/**
 * Distribute leftover horizontal space across the flexible/content columns so
 * the visible columns fill `tableWidth`, working from an already-resolved map of
 * base widths (per-column defaults, or persisted/saved widths). Fixed-pixel and
 * tiny icon columns keep their base width; flexible columns grow in proportion
 * to their current width. If the base widths already fill or overflow, or there
 * is nothing flexible to grow, the base widths are returned untouched.
 */
export const fillColumnWidths = (
  columns: any[],
  baseWidths: Record<string, number | 'auto'>,
  tableWidth: any,
  {
    hiddenColumnNames = [],
  }: Pick<ComputeColumnWidthsOptions, 'hiddenColumnNames'> = {}
): Record<string, number | 'auto'> => {
  const numericVisible = visibleNumericColumns(
    columns,
    baseWidths,
    hiddenColumnNames
  );

  const leftover = tableWidth - totalColumnWidth(numericVisible, baseWidths);
  if (leftover <= 0) {
    return { ...baseWidths };
  }

  const flexColumns = numericVisible.filter(isFlexibleColumn);
  const flexBaseTotal = flexColumns.reduce(
    (acc, column) => acc + (baseWidths[column.name] as number),
    0
  );
  if (flexColumns.length === 0 || flexBaseTotal <= 0) {
    return { ...baseWidths };
  }

  const widths = { ...baseWidths };
  // Floor each column's proportional share (so no share is ever negative), then
  // hand out the leftover 1px rounding remainder to the widest columns first.
  // This fills the width exactly without ever shrinking a column below its base.
  const extras = flexColumns.map((column) =>
    Math.floor((leftover * (baseWidths[column.name] as number)) / flexBaseTotal)
  );
  // Floored too: base widths come from rem, so they are fractional, and handing
  // out a whole pixel for a 0.44px remainder overran the table by the rest and
  // put a 1px horizontal scrollbar under it.
  const remainder = Math.floor(
    leftover - extras.reduce((sum, extra) => sum + extra, 0)
  );
  const widestFirst = flexColumns
    .map((column, index) => ({
      index,
      base: baseWidths[column.name] as number,
    }))
    .sort((a, b) => b.base - a.base);
  for (let i = 0; i < remainder; i += 1) {
    extras[widestFirst[i].index] += 1;
  }
  flexColumns.forEach((column, index) => {
    widths[column.name] = (baseWidths[column.name] as number) + extras[index];
  });

  return widths;
};

/**
 * Resolve a width for every column. By default this is just the per-column base
 * width (`calculateColumnWidth`). When `fill` is enabled and the visible columns
 * do not already use the full table width, the leftover space is distributed
 * across the flexible/content columns so the table fills the available
 * horizontal real estate instead of leaving a gap.
 */
export const computeColumnWidths = (
  columns: any[],
  tableWidth: any,
  { fill = false, hiddenColumnNames = [] }: ComputeColumnWidthsOptions = {}
): Record<string, number | 'auto'> => {
  const base: Record<string, number | 'auto'> = {};
  columns.forEach((column) => {
    base[column.name] = calculateColumnWidth(column, tableWidth);
  });

  if (!fill) {
    return base;
  }

  return fillColumnWidths(columns, base, tableWidth, { hiddenColumnNames });
};

/** The `[{ columnName, width }]` entries keyed by name, as fill reads them. */
const widthEntriesToBase = (currentWidths: readonly any[]) => {
  const base: Record<string, number | 'auto'> = {};
  currentWidths.forEach(({ columnName, width }) => {
    base[columnName] = width;
  });
  return base;
};

/**
 * Whether the widths the table is currently rendering already occupy more than
 * `tableWidth` — the same measurement `fillColumnWidths` makes to find leftover
 * space, asked from the outside.
 *
 * Fill only ever hands leftover space out, so it has no answer for a container
 * that got *narrower*: there is no leftover, and the widths stay at the total
 * they were last seeded at. Callers ask this to decide whether to rebase off
 * the column configuration instead of growing what is on screen.
 */
export const columnWidthEntriesOverflow = (
  columns: any[],
  currentWidths: readonly any[],
  tableWidth: number,
  hiddenColumnNames: string[] = []
): boolean => {
  const base = widthEntriesToBase(currentWidths);
  const visible = visibleNumericColumns(columns, base, hiddenColumnNames);

  return totalColumnWidth(visible, base) > tableWidth;
};

/**
 * Fill widths in the `[{ columnName, width }]` shape `TableColumnResizing`
 * consumes, grown from the widths the table is *currently* rendering.
 *
 * Fill has to drive the resizing plugin's state rather than `Table`'s
 * `columnExtensions`: the plugin mounts after `Table` and overwrites those
 * widths, so the two features cannot both read from `columnExtensions`
 * (FIR-20964). Basing the fill on the current widths also means a layout that
 * already spans the container — a restored manual one — comes back untouched,
 * since `fillColumnWidths` is a no-op once there is no leftover space.
 */
export const fillColumnWidthEntries = (
  columns: any[],
  currentWidths: readonly any[],
  tableWidth: number,
  hiddenColumnNames: string[] = []
) => {
  const filled = fillColumnWidths(
    columns.map(applyColumnSizePreset),
    widthEntriesToBase(currentWidths),
    tableWidth,
    { hiddenColumnNames }
  );

  return currentWidths.map((entry) => ({
    ...entry,
    width: filled[entry.columnName] ?? entry.width,
  }));
};

/**
 * Fill widths in the same entry shape, but derived from each column's
 * *configured* width instead of the width the table is currently rendering.
 *
 * Growing the current widths (`fillColumnWidthEntries`) only ever ratchets
 * upward, which is fine for a container resize but wrong when the set of
 * visible columns changes: hiding columns lets the survivors expand to fill the
 * container, so once the hidden ones come back their inflated bases already
 * overflow it, fill sees no leftover space and does nothing, and the table stays
 * wider than its container with the recovered columns pushed out of view
 * (FIR-20967). Re-deriving from the configuration makes a visibility change
 * land on the same layout the table would have had if those columns had been
 * visible all along, rather than compounding the previous fill.
 */
export const rebaseColumnWidthEntries = (
  columns: any[],
  currentWidths: readonly any[],
  tableWidth: number,
  hiddenColumnNames: string[] = []
) => {
  const filled = computeColumnWidths(
    columns.map(applyColumnSizePreset),
    tableWidth,
    { fill: true, hiddenColumnNames }
  );

  return currentWidths.map((entry) => ({
    ...entry,
    width: filled[entry.columnName] ?? entry.width,
  }));
};
